/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect } from 'react';

import { Grid, Hidden, breakpoints } from '@coursera/cds-core';

import PaginationControls from 'bundles/page/components/cds/PaginationControls';
import Breadcrumbs from 'bundles/shared-articles/components/ArticleBreadcrumbs';
import HeroBanner from 'bundles/shared-articles/components/ArticleHubHeroBanner';
import type { ArticleUrlDataProps as UrlDataProps } from 'bundles/shared-articles/hooks/useArticleUrlData';
import CategoryNavigation from 'bundles/unified-seo-content-common/components/hub-page/CategoryNavigation';
import ContentCards from 'bundles/unified-seo-content-common/components/hub-page/ContentCards';
import Layout from 'bundles/unified-seo-content-common/components/hub-page/Layout';
import ProductRecommendationsDelegator from 'bundles/unified-seo-content-common/components/hub-page/ProductRecommendationsDelegator';
import RichTextContentRenderer from 'bundles/unified-seo-content-common/components/rich-text/RichTextContentRenderer';
import CategoryPageSEO from 'bundles/unified-seo-content-common/components/seo/CategoryPageSEO';
import {
  ITEMS_PER_CATEGORY_PAGE,
  NUMBER_OF_ITEMS_PER_LINE_DESKTOP,
  NUMBER_OF_ITEMS_PER_LINE_MOBILE,
  NUMBER_OF_RECOMMENDATIONS_TO_SHOW_ON_DESKTOP,
  SEO_CONTENT_PAGE_TYPE,
} from 'bundles/unified-seo-content-common/constants';
import type { ReturnValues as RecommendationCollections } from 'bundles/unified-seo-content-common/hooks/useDiscoveryCollectionsRecs';
import type { PageDataProps as IntermediatePageData } from 'bundles/unified-seo-content-common/hooks/useSeoContentCategoryPageQuery';
import type { PageDataProps as ResourcesData } from 'bundles/unified-seo-content-common/hooks/useSeoContentFilterQuery';
import { scrollPageToTop } from 'bundles/unified-seo-content-common/utils';

import _t from 'i18n!nls/resources';

export type PageData = Omit<IntermediatePageData, 'error' | 'contentFilters' | 'isHubPageDataLoading'> &
  RecommendationCollections &
  Pick<ResourcesData, 'contentList' | 'totalNumberOfContent'> & {
    hubPageLink: string;
    productRecsIndexName: string;
  };

type Props = {
  pageData: PageData;
  currentPageNumber: number;
  pageCount: number;
} & Pick<UrlDataProps, 'slug' | 'pathname'>;

const styles = {
  rowsContainer: css`
    display: flex;
    flex-direction: column;
  `,
  fullWidthContainerContent: css`
    margin: 0 auto;
  `,
  fullWidthContainer: css`
    width: calc(100% + var(--cds-spacing-600));
    border-radius: var(--cds-spacing-100);
    background-color: var(--cds-color-neutral-background-primary-weak);
    padding: var(--cds-spacing-300);
    margin-left: calc(var(--cds-spacing-300) * -1);
    margin-bottom: var(--row-spacing-desktop-in-px);
    ${breakpoints.down('md')} {
      margin-bottom: var(--row-spacing-mobile-in-px);
    }
  `,
  pagination: css`
    margin-bottom: var(--row-spacing-desktop-in-px);
    ${breakpoints.down('md')} {
      margin-bottom: var(--row-spacing-mobile-in-px);
    }
  `,
};

export const CategoryPage = ({ pageData, slug, pathname, currentPageNumber, pageCount }: Props) => {
  useEffect(() => {
    scrollPageToTop();
  }, [currentPageNumber]);

  const {
    title,
    introduction,
    featuredImageUrl,
    featuredImageAlt,
    featuredContent,
    bottomContentLinks,
    bottomContentJson,
    discoveryCollectionSetId,
    productRecommendationTitle,
    productRecommendationsSearchUrl,
    navigationLinks,
    contentList,
    totalNumberOfContent,
    recommendationCollections,
    loadingRecommendations,
    productRecsIndexName,
    hubPageLink,
  } = pageData;

  const missingRequiredFields = !contentList || !title || !introduction || !featuredImageUrl || !featuredContent;

  if (missingRequiredFields) return null;

  const hasDiscoveryCollectionRecs = !!recommendationCollections.length;
  const shouldShowProductRecsSection = hasDiscoveryCollectionRecs || !!productRecommendationsSearchUrl;
  const hasMultipleDiscoveryCollectionRecs = hasDiscoveryCollectionRecs && recommendationCollections.length > 1;

  const shouldShowFeaturedContent = currentPageNumber === 1;

  const pageType = SEO_CONTENT_PAGE_TYPE.resources;

  return (
    <React.Fragment>
      <Layout
        heroBannerComponent={
          <HeroBanner
            title={title}
            subtitle={introduction}
            image={{ src: featuredImageUrl, alt: featuredImageAlt || _t('Hero Banner') }}
            breadCrumbComponent={<Breadcrumbs title={title} links={[{ label: _t('Resources'), href: hubPageLink }]} />}
          />
        }
        navigationComponent={<CategoryNavigation activeCategorySlug={slug} navigationLinks={navigationLinks} />}
        contentComponent={
          <div
            css={[
              styles.rowsContainer,
              css`
                --row-spacing-desktop-in-px: var(--cds-spacing-800);
                --row-spacing-mobile-in-px: var(--cds-spacing-400);
              `,
            ]}
          >
            {shouldShowFeaturedContent && (
              <ContentCards contentList={featuredContent} isFeaturedContentRow pageType={pageType} />
            )}
            <Hidden smDown>
              <ContentCards contentList={contentList.slice(0, NUMBER_OF_ITEMS_PER_LINE_DESKTOP)} pageType={pageType} />
            </Hidden>
            <Hidden mdUp>
              <ContentCards contentList={contentList.slice(0, NUMBER_OF_ITEMS_PER_LINE_MOBILE)} pageType={pageType} />
            </Hidden>
            {shouldShowProductRecsSection && (
              <div css={styles.fullWidthContainer}>
                <div css={styles.fullWidthContainerContent}>
                  <ProductRecommendationsDelegator
                    shouldUseDiscoveryCollections={!!discoveryCollectionSetId}
                    recommendationCollections={hasDiscoveryCollectionRecs ? [recommendationCollections[0]] : []}
                    loadingDiscoveryCollectionsRecommendations={loadingRecommendations}
                    productRecsByAlgoliaMetadata={{
                      title,
                      productRecommendationTitle,
                      productRecommendationsSearchUrl,
                      productRecsIndexName,
                      hitsPerPage: NUMBER_OF_RECOMMENDATIONS_TO_SHOW_ON_DESKTOP,
                    }}
                  />
                </div>
              </div>
            )}
            <Hidden smDown>
              <ContentCards contentList={contentList.slice(NUMBER_OF_ITEMS_PER_LINE_DESKTOP)} pageType={pageType} />
            </Hidden>
            <Hidden mdUp>
              <ContentCards contentList={contentList.slice(NUMBER_OF_ITEMS_PER_LINE_MOBILE)} pageType={pageType} />
            </Hidden>
            {hasMultipleDiscoveryCollectionRecs && (
              <div css={styles.fullWidthContainer}>
                <div css={styles.fullWidthContainerContent}>
                  <ProductRecommendationsDelegator
                    recommendationCollections={recommendationCollections.slice(1)}
                    loadingDiscoveryCollectionsRecommendations={loadingRecommendations}
                    productRecsByAlgoliaMetadata={{
                      title,
                      productRecommendationTitle: null,
                      productRecommendationsSearchUrl: null,
                      productRecsIndexName,
                      hitsPerPage: 0,
                    }}
                    shouldUseDiscoveryCollections
                  />
                </div>
              </div>
            )}
            {totalNumberOfContent && totalNumberOfContent > ITEMS_PER_CATEGORY_PAGE && (
              <Grid container justifyContent="center" css={styles.pagination}>
                <Grid item>
                  <PaginationControls
                    pageCount={pageCount}
                    currentPage={currentPageNumber}
                    maxPages={5}
                    renderLinks
                    largeStyle
                  />
                </Grid>
              </Grid>
            )}
            {bottomContentJson && <RichTextContentRenderer body={bottomContentJson} links={bottomContentLinks} />}
          </div>
        }
      />
      <CategoryPageSEO
        pageData={pageData}
        hubPageLink={hubPageLink}
        pageType={pageType}
        currentPageNumber={currentPageNumber}
        pageCount={pageCount}
        pathname={pathname}
      />
    </React.Fragment>
  );
};

export default CategoryPage;
